﻿import emojiBaseDataGroups from 'emojibase-data/meta/groups.json';
import emojis from 'emojibase-data/en/data.json';
import { View, ButtonView, LabelView } from 'ckeditor5';
import { Collection } from 'ckeditor5';

// extract groups from emojibase-data
const { groups } = emojiBaseDataGroups;
const allTiles = new Collection();

const emojiCategories = Object.keys(groups).map(categoryKey => ({ order: parseInt(categoryKey), group: groups[categoryKey] }));

// "merge" emoji list with category info
const emojisWithCategory = emojis.map(e => ({ ...e, emojiCategory: emojiCategories[e.group] }))
    // filter out emojis without category
    .filter(e => e.emojiCategory !== undefined && e.version < 13)
    // sort by category order then by emoji order
    .sort((a, b) => {
        if (a.emojiCategory.order < b.emojiCategory.order) {
            return -1;
        }
        else if (a.emojiCategory.order > b.emojiCategory.order) {
            return 1;
        }
        else {
            if (a.order < b.order) {
                return -1;
            }
            else if (a.order > b.order) {
                return 1;
            }
        }

        return 0;
    });

// Create a "tile" (button) for the specified emoji item
function createTile(item, locale) {
    const tile = new ButtonView(locale);
    const { t } = locale;

    // extract emoji and hexcode from item
    const { emoji, hexcode } = item;
    const character = emoji;
    const name = t(`Emoji_${hexcode}`);

    tile.set({
        label: character,
        withText: true,
        class: 'ck-character-grid__tile ck-emoji-grid-font-size',
        tags: item.tags
    });

    // add category info for filter
    tile.set('emojiCategory', item.group);

    tile.extendTemplate({
        attributes: {
            title: name
        },
        on: {
            mouseover: tile.bindTemplate.to('mouseover'),
        }
    });

    tile.on('mouseover', () => {
        tile.activeView.fire('tileHover', { name, character });
    });

    tile.on('execute', () => {
        tile.activeView.fire('execute', { name, character });
    });

    return tile;
}

function createEmojiListView(locale) {
    const { t } = locale;

    const view = new View(locale);
    view.set('tiles', view.createCollection());

    const bind = view.bindTemplate;
    view.setTemplate({
        tag: 'div',
        children: [
            // emoji list
            {
                tag: 'div',
                children: [
                    {
                        tag: 'div',
                        attributes: {
                            class: [
                                'ck',
                                'ck-character-grid__tiles',
                                'ck-emoji-grid__tiles',
                            ]
                        },
                        children: view.tiles
                    }
                ],
                attributes: {
                    class: [
                        'ck',
                        'ck-character-grid',
                        'ck-emoji-grid'
                    ]
                }
            },
            // loader
            {
                tag: 'div',
                attributes: {
                    class: [
                        'loader'
                    ]
                }
            },
        ],
        attributes: {
            class: [
                'ck-emoji-grid-wrapper',
                bind.to('loadingClass')
            ]
        }
    });

    view.on('change:filterCategory', (evt, data) => {
        view.set('categoryFilter', data);
        filterTiles(view);
    });

    view.on('change:searchExpression', (evt, data) => {
        view.set('searchText', data);
        filterTiles(view);
    });

    allTiles.clear();
    view.set('loadingClass', 'is-loading');

    window.setTimeout(() => {
        let tilesToAdd = [];

        let categoryName = null;
        let i = 0, length = emojisWithCategory.length;
        let element = null;

        while (i < length) {
            element = emojisWithCategory[i];

            if (categoryName !== element.emojiCategory.group) {
                categoryName = element.emojiCategory.group;

                const groupLabel = new LabelView(locale);
                groupLabel.set({
                    text: t(`EmojiCategory_${categoryName}`),
                    'emojiCategory': element.group,
                    'isCategoryHeadline': true
                });

                tilesToAdd.push(groupLabel);
            }

            tilesToAdd.push(createTile(element, locale));
            i++;
        }

        allTiles.addMany(tilesToAdd);

        filterTiles(view);

        view.set('loadingClass', '');
    }, 0);

    return view;
}

function filterTiles(view) {
    let category = view.categoryFilter;
    let searchExpressions = (view.searchText || '').split(' ').filter(expr => expr);

    function filterBySearchExpression(el) {
        return searchExpressions.length === 0 || searchExpressions.every(expr => {
            return el.isCategoryHeadline === true ||
                el.label.toLowerCase().indexOf(expr.toLowerCase()) >= 0 ||
                el.tags.some(t => t.toLowerCase().indexOf(expr.toLowerCase()) >= 0);
        });
    }

    // filter out empty category headers
    // can occur if no emoji in this category matches the search expression
    function filterEmptyCategories(element, index, array) {
        // remove the current element if the element is a category headline
        // and no next element exists of the next element is also a category headline
        if (element.isCategoryHeadline && (index === array.length - 1 || array[index + 1].isCategoryHeadline)) {
            return false;
        }

        return true;
    }

    let tiles = view.tiles;
    tiles.clear();

    if (category === 'All') {
        tiles.addMany(allTiles.filter(filterBySearchExpression).filter(filterEmptyCategories));
    }
    else {
        tiles.addMany(allTiles.filter(t => t.emojiCategory == category).filter(filterBySearchExpression).filter(filterEmptyCategories));
    }

    // set current view for firing events to insert emoji into correct editor instance
    tiles.forEach(tile => {
        tile.set('activeView', view);
    });
}

export { createEmojiListView };